import { Outlet } from "react-router-dom";
import { useState, useEffect } from "react";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { MobileTabBar } from "./MobileTabBar";
import { SuspensionScreen } from "./SuspensionScreen";
import { LicenseEntryScreen } from "./LicenseEntryScreen";
import { SecurityBanner } from "./SecurityBanner";
import { LicenseBanner } from "./LicenseBanner";
import { useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import { useAuthStore, type User } from "@/lib/auth";

type LicenseStatusPublic = {
  enabled: boolean;
  mode: string;
  message: string;
};

/**
 * Layout protegido: sidebar + topbar + outlet.
 * Asegura que el /me esté cargado y sincronizado con el store.
 * Si la licencia está en modo "blocked", muestra SuspensionScreen en su lugar.
 */
export function Layout() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("byteisp.sidebar") === "collapsed";
  });
  // Drawer móvil (<md): la sidebar vive off-canvas y se abre con la hamburguesa
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("byteisp.sidebar", collapsed ? "collapsed" : "expanded");
  }, [collapsed]);

  const setUser = useAuthStore((s) => s.setUser);

  useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const user = await apiCall<User>("get", "/api/v1/me");
      setUser(user);
      return user;
    },
    staleTime: 60_000,
  });

  // Verifica estado de licencia — si está bloqueada muestra pantalla de suspensión.
  // staleTime corto para detectar rápido cambios de estado (reactivación).
  const { data: licStatus } = useQuery<LicenseStatusPublic>({
    queryKey: ["licensing-status"],
    queryFn: () => apiCall<LicenseStatusPublic>("get", "/api/v1/licensing/status"),
    staleTime: 30_000,
    refetchInterval: 60_000,
    // Si falla (ej. 402 ya bloqueado), igual dejamos pasar — el middleware lo manejará
    retry: false,
  });

  // Solo bloqueamos cuando licensing está activo Y mode=blocked.
  // Sistema notice/banner v1.0.38: warning|grace|stale NO bloquean — pasan al
  // Layout normal y LicenseBanner muestra el aviso arriba.
  const isBlocked = licStatus?.enabled && licStatus.mode === "blocked";
  // Sin licencia o clave rechazada → pantalla de ingreso de clave
  const needsLicense = licStatus?.enabled && licStatus.mode === "no_license";

  if (isBlocked) return <SuspensionScreen />;
  if (needsLicense) return <LicenseEntryScreen />;

  return (
    <div className="flex h-full w-full">
      {/* Scrim móvil */}
      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/55 md:hidden"
          onClick={() => setNavOpen(false)}
        />
      )}
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((v) => !v)}
        mobileOpen={navOpen}
        onMobileClose={() => setNavOpen(false)}
      />
      {/* rc146.2 — escritorio: columna derecha con dos paneles flotantes (barra
          superior delgada + panel de contenido que scrollea por dentro), mismo
          alto total que la barra lateral. Celular sin cambios. */}
      <div className="flex flex-1 flex-col overflow-hidden md:gap-3 md:p-3">
        <Topbar onMenu={() => setNavOpen(true)} />
        {/* overflow-x-hidden: la página NUNCA panea lateral; lo ancho (tablas)
            scrollea dentro de su propio wrapper (ui/table.tsx overflow-auto) */}
        {/* rc136 — en móvil la barra de pestañas (abajo) ya cubre el home
            indicator, así que el main solo lleva su padding normal ahí. */}
        <main className="glass-panel flex-1 overflow-y-auto overflow-x-hidden bg-transparent p-3 md:rounded-[14px] md:border md:border-border md:p-4 md:pb-[calc(1rem+env(safe-area-inset-bottom))]">
          <LicenseBanner />
          <SecurityBanner />
          <Outlet />
        </main>
        <MobileTabBar onMore={() => setNavOpen((v) => !v)} menuOpen={navOpen} />
      </div>
    </div>
  );
}
